import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import api from '../services/api';
import { PUBLIC_PATHS } from '../constants/publicPaths';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);

  // Fetch profile completion status
  const fetchProfileStatus = useCallback(async () => {
    try {
      const response = await api.get('/users/me/profile-status');
      setProfileStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch profile status:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check on public routes (signing, portal login, etc.)
      const publicPaths = PUBLIC_PATHS;
      const isPublicRoute = publicPaths.some(p => window.location.pathname.includes(p));
      if (isPublicRoute) {
        setLoading(false);
        return;
      }

      // Try to get current user - if httpOnly cookie is valid, this will work
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        // Note: We keep the localStorage token for backward compatibility
        // Many components still use localStorage.getItem('token') for API calls
        // Fetch profile completion status
        await fetchProfileStatus();
      } catch (error) {
        // Not authenticated or token expired
        console.error('Failed to get user:', error);
        setUser(null);
        setIsAuthenticated(false);
        // Clean up localStorage items only on auth failure
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      setLoading(false);
    };
    initAuth();
  }, [fetchProfileStatus]);

  const login = async (username, password, totpCode = null, webauthnResponse = null) => {
    const data = await authService.login(username, password, totpCode, webauthnResponse);
    // Cookie is set by the server, no need to store in localStorage
    // But keep localStorage for backward compatibility with any code that checks it
    localStorage.setItem('token', data.access_token);

    // Generate a unique session ID for this login session
    // Used by WhileYouWereGoneWidget to track dismissal state per session
    const sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('sessionId', sessionId);
    // Clear any previous session's dismissed state
    localStorage.removeItem('whileYouWereGoneDismissed');

    const userData = await authService.getCurrentUser();
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));

    // Fetch profile completion status after login
    await fetchProfileStatus();

    return data;
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clean up local state regardless of API call result
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Role checking helpers
  const role = user?.role || null;
  const isAdmin = role === 'admin';
  const isAttorney = role === 'attorney';
  const isStaff = role === 'staff' || role === 'admin';  // Admins have staff privileges
  const isClient = role === 'client';
  const isFirmMember = role === 'admin' || role === 'staff' || role === 'attorney';  // Internal firm users

  // Profile completion helpers
  const isProfileComplete = profileStatus?.overall_complete || false;
  const profileCompletionPercentage = profileStatus?.profile_completion_percentage || 0;

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    role,
    isAdmin,
    isAttorney,
    isStaff,
    isClient,
    isFirmMember,
    profileStatus,
    isProfileComplete,
    profileCompletionPercentage,
    refreshProfileStatus: fetchProfileStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
